(function (root, factory) {
  var api = factory(root);

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.ZugAcrylicSignRecipe = api;
    if (root.document) api.start();
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  var DIMENSION_TOLERANCE = 0.005;

  function money(cents, currencyCode) {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(cents || 0) / 100);
  }

  function uid() {
    if (root.crypto && typeof root.crypto.randomUUID === 'function') {
      return 'acrylic-sign-' + root.crypto.randomUUID();
    }
    return 'acrylic-sign-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  }

  function parseDimensions(sizeLabel) {
    var matches = String(sizeLabel || '').match(/([0-9.]+)[^0-9.]+([0-9.]+)/);
    return {
      width: matches ? Number(matches[1]) : NaN,
      height: matches ? Number(matches[2]) : NaN
    };
  }

  function formatDimension(value) {
    var number = Number(value);
    if (!Number.isFinite(number)) return '';
    return number.toFixed(2).replace(/\.?0+$/, '');
  }

  function dimensionLabel(width, height) {
    return formatDimension(width) + '″ × ' + formatDimension(height) + '″';
  }

  function validateDimensions(width, height) {
    var widthValue = Number(width);
    var heightValue = Number(height);

    if (!Number.isFinite(widthValue) || !Number.isFinite(heightValue)) {
      return {
        valid: false,
        message: 'Enter a width and height.'
      };
    }

    if (widthValue < 10 || heightValue < 10) {
      return {
        valid: false,
        message: 'Minimum size is 10″ × 10″.'
      };
    }

    if (widthValue > 24 || heightValue > 24) {
      return {
        valid: false,
        message: 'The other side cannot exceed 24″.'
      };
    }

    if (Math.min(widthValue, heightValue) > 18) {
      return {
        valid: false,
        message: 'One side must be 18″ or less.'
      };
    }

    return {
      valid: true,
      message: '',
      width: widthValue,
      height: heightValue,
      shorter: Math.min(widthValue, heightValue),
      longer: Math.max(widthValue, heightValue)
    };
  }

  function nativeSizeForDimensions(width, height, sizeLabels) {
    var validation = validateDimensions(width, height);
    if (!validation.valid) return '';

    var shorter = validation.shorter;
    var longer = validation.longer;
    var match = (Array.isArray(sizeLabels) ? sizeLabels : []).find(function (sizeLabel) {
      var dimensions = parseDimensions(sizeLabel);
      if (!Number.isFinite(dimensions.width) || !Number.isFinite(dimensions.height)) return false;
      return Math.abs(Math.min(dimensions.width, dimensions.height) - shorter) <= DIMENSION_TOLERANCE &&
        Math.abs(Math.max(dimensions.width, dimensions.height) - longer) <= DIMENSION_TOLERANCE;
    });

    return match || '';
  }

  function propertiesFor(state, variant, calculator, metrics) {
    var upload = root.__ZUG_UPLOAD || {};
    var artworkMethod = upload.artwork_mode === 'email_later'
      ? 'Upload Later'
      : upload.artwork_mode === 'reorder' ? 'Reorder' : 'Upload Artwork';
    var properties = {
      'Product Type': 'Acrylic Sign',
      'Shape': state.shapeLabel,
      'Size': state.sizeLabel,
      'Quantity': String(metrics.displayQuantity),
      'Mounting': state.mountingLabel,
      'Turnaround': state.turnaroundLabel,
      'Artwork Method': artworkMethod,
      'calc_line_uid': state.configUid,
      '_pricing_engine': 'true',
      '_line_type': 'base',
      '_product_family': 'acrylic',
      '_acrylic_subtype': 'sign',
      '_bundle_display_quantity': String(metrics.displayQuantity),
      '_bundle_quantity_locked': 'true',
      '_total_desired': (metrics.totalCents / 100).toFixed(2),
      '_acrylic_sign_mounting_key': state.mounting,
      '_acrylic_sign_turnaround_key': state.turnaround,
      '_acrylic_sign_expected_total_cents': String(metrics.totalCents),
      'ProductHandle': calculator.dataset.productHandle || '',
      'ProductImage': calculator.dataset.productImage || '',
      'ProductUrl': calculator.dataset.productUrl || ''
    };

    if (state.isCustomSize) {
      properties.Width = formatDimension(state.customWidth) + '″';
      properties.Height = formatDimension(state.customHeight) + '″';
    }
    if (upload.note) properties['Order Notes'] = String(upload.note);
    if (upload.artwork_mode === 'reorder' && upload.previous_order_number) {
      properties['Previous Order Number'] = String(upload.previous_order_number);
    }
    if (upload.artwork_mode === 'instant_proof') {
      properties['Uploaded Artwork'] = upload.upload_id || upload.original_url ? 'Received' : 'Pending';
      properties['Preview Approval'] = upload.approved ? 'Approved' : 'Not Approved';
    }
    if (upload.upload_id) properties._upload_id = String(upload.upload_id);
    if (upload.original_url) properties._artwork_original_url = String(upload.original_url);
    if (upload.preview_url) properties._dcf_preview_url = String(upload.preview_url);
    if (upload.approved != null) properties._artwork_approved = upload.approved ? 'true' : 'false';
    if (upload._placement_state) properties._placement_state = String(upload._placement_state);
    if (upload.preview_transform) properties._preview_transform = String(upload.preview_transform);
    if (upload.shape_mode) properties._dcf_shape_mode = String(upload.shape_mode);
    if (upload.physical_width_in) properties._physical_width_in = String(upload.physical_width_in);
    if (upload.physical_height_in) properties._physical_height_in = String(upload.physical_height_in);
    if (upload.bbox_width_px) properties._bbox_width_px = String(upload.bbox_width_px);
    if (upload.bbox_height_px) properties._bbox_height_px = String(upload.bbox_height_px);
    properties._shopify_variant_title = variant.public_title || variant.title || '';

    Object.keys(properties).forEach(function (key) {
      if (properties[key] === '') delete properties[key];
    });
    return properties;
  }

  function initialize(calculator) {
    if (calculator.dataset.zugAcrylicSignReady === 'true') return;
    calculator.dataset.zugAcrylicSignReady = 'true';

    var submit = calculator.querySelector('[data-acrylic-sign-submit]');
    var status = calculator.querySelector('[data-acrylic-sign-status]');
    var sizeError = calculator.querySelector('[data-acrylic-sign-size-error]');
    var customSizePanel = calculator.querySelector('[data-acrylic-sign-custom-size]');
    var customWidthInput = calculator.querySelector('[data-acrylic-sign-custom-width]');
    var customHeightInput = calculator.querySelector('[data-acrylic-sign-custom-height]');
    var optionScript = calculator.querySelector('[data-acrylic-sign-options]');
    var variantScript = calculator.querySelector('[data-acrylic-sign-variants]');
    var sizeButtons = Array.from(calculator.querySelectorAll('[data-acrylic-sign-size]'));
    var quantityButtons = Array.from(calculator.querySelectorAll('[data-acrylic-sign-quantity]'));
    var shapeButtons = Array.from(calculator.querySelectorAll('[data-acrylic-sign-shape]'));
    var mountingButtons = Array.from(calculator.querySelectorAll('[data-acrylic-sign-mounting]'));
    var turnaroundButtons = Array.from(calculator.querySelectorAll('[data-acrylic-sign-turnaround]'));
    var pricingToggle = calculator.querySelector('[data-acrylic-sign-pricing-toggle]');
    var pricingToggleText = calculator.querySelector('[data-acrylic-sign-pricing-toggle-text]');
    var pricingList = calculator.querySelector('[data-acrylic-sign-pricing-list]');
    var pricingRows = Array.from(calculator.querySelectorAll('[data-acrylic-sign-pricing-row]'));
    var options = [];
    var variants = [];
    var adjustmentVariants = null;
    var adding = false;
    var pricingListOpen = false;

    try {
      options = JSON.parse(optionScript ? optionScript.textContent : '[]');
      variants = JSON.parse(variantScript ? variantScript.textContent : '[]');
    } catch (error) {
      options = [];
      variants = [];
    }

    function optionIndex(name) {
      var normalized = String(name || '').trim().toLowerCase();
      return options.findIndex(function (optionName) {
        return String(optionName || '').trim().toLowerCase() === normalized;
      });
    }

    var sizeIndex = optionIndex('Size');
    var quantityIndex = optionIndex('Quantity');
    var nativeSizeLabels = Array.from(new Set(variants.map(function (variant) {
      return variant.options[sizeIndex];
    }).filter(Boolean)));
    var selectedVariantId = Number(calculator.dataset.selectedVariantId);
    var initialVariant = variants.find(function (variant) {
      return Number(variant.id) === selectedVariantId;
    }) || variants.find(function (variant) { return variant.available; }) || variants[0] || null;
    var initialDimensions = parseDimensions(initialVariant && initialVariant.options[sizeIndex]);
    var initialIsCustom = calculator.dataset.selectedSizeIsPreset !== 'true';
    var selectedShape = shapeButtons.find(function (button) {
      return button.classList.contains('acr-calc__icon-btn--active');
    }) || shapeButtons[0];
    var selectedMounting = mountingButtons.find(function (button) {
      return button.classList.contains('acr-calc__btn--active');
    }) || mountingButtons[0];
    var selectedTurnaround = turnaroundButtons.find(function (button) {
      return button.classList.contains('acr-calc__btn--active');
    }) || turnaroundButtons[0];
    var state = {
      size: initialVariant && sizeIndex !== -1 ? initialVariant.options[sizeIndex] : '',
      sizeLabel: initialVariant && sizeIndex !== -1
        ? dimensionLabel(initialDimensions.width, initialDimensions.height)
        : '',
      isCustomSize: initialIsCustom,
      customWidth: Number.isFinite(initialDimensions.width) ? initialDimensions.width : 10,
      customHeight: Number.isFinite(initialDimensions.height) ? initialDimensions.height : 10,
      quantity: initialVariant && quantityIndex !== -1 ? String(initialVariant.options[quantityIndex]) : '',
      shape: selectedShape ? selectedShape.dataset.acrylicSignShape : 'custom',
      shapeLabel: selectedShape ? selectedShape.dataset.label : 'Custom Shape',
      mounting: selectedMounting ? selectedMounting.dataset.acrylicSignMounting : 'mounting_hole',
      mountingLabel: selectedMounting ? selectedMounting.dataset.label : 'Mounting Hole Included',
      mountingCents: selectedMounting ? Number(selectedMounting.dataset.adjustmentCents) : 0,
      turnaround: selectedTurnaround ? selectedTurnaround.dataset.acrylicSignTurnaround : 'regular',
      turnaroundLabel: selectedTurnaround ? selectedTurnaround.dataset.label : 'Regular',
      turnaroundDisplayLabel: selectedTurnaround ? selectedTurnaround.dataset.displayLabel : 'Regular',
      turnaroundMultiplier: selectedTurnaround ? Number(selectedTurnaround.dataset.multiplier) : 1,
      variant: initialVariant,
      metrics: null,
      configUid: uid()
    };

    customWidthInput.value = formatDimension(state.customWidth);
    customHeightInput.value = formatDimension(state.customHeight);
    customSizePanel.hidden = !state.isCustomSize;

    function setStatus(message, error) {
      status.textContent = message || '';
      status.classList.toggle('is-error', Boolean(error));
    }

    function setSizeError(message) {
      var hasError = Boolean(message);
      sizeError.textContent = message || '';
      sizeError.hidden = !hasError;
      customWidthInput.setAttribute('aria-invalid', hasError ? 'true' : 'false');
      customHeightInput.setAttribute('aria-invalid', hasError ? 'true' : 'false');
    }

    function invalidateArtwork() {
      root.__ZUG_UPLOAD = null;
    }

    function resolvedNativeSize() {
      if (!state.isCustomSize) return state.size;
      return nativeSizeForDimensions(state.customWidth, state.customHeight, nativeSizeLabels);
    }

    function currentSizeValidation() {
      if (!state.isCustomSize) return { valid: true, message: '' };
      return validateDimensions(state.customWidth, state.customHeight);
    }

    function variantForState(size, quantity) {
      if (!size) return null;
      return variants.find(function (variant) {
        return variant.options[sizeIndex] === size &&
          String(variant.options[quantityIndex]) === String(quantity);
      }) || null;
    }

    function metricsForVariant(variant, quantity) {
      if (!variant || !variant.available || !root.ZugBannerPricing ||
          typeof root.ZugBannerPricing.variantRecipeMetrics !== 'function') return null;
      try {
        return root.ZugBannerPricing.variantRecipeMetrics({
          variantPriceCents: Number(variant.price),
          addonCents: state.mountingCents,
          tierQuantity: Number(quantity),
          turnaroundMultiplier: state.turnaroundMultiplier,
          quantityMode: 'variant-tier'
        });
      } catch (error) {
        return null;
      }
    }

    function labelValue(key, text) {
      var value = calculator.querySelector('[data-acrylic-sign-value="' + key + '"]');
      var summary = calculator.querySelector('[data-acrylic-sign-summary="' + key + '"]');
      if (value) value.textContent = text;
      if (summary) summary.textContent = text;
    }

    function renderSizeButtons() {
      sizeButtons.forEach(function (button) {
        var isCustom = button.dataset.optionValue === 'custom';
        var selected = isCustom ? state.isCustomSize : !state.isCustomSize && button.dataset.optionValue === state.size;
        var candidate = isCustom ? true : variantForState(button.dataset.optionValue, state.quantity);
        button.classList.toggle('acr-calc__btn--active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.disabled = !isCustom && !(candidate && candidate.available);
      });
    }

    function renderQuantityButtons(nativeSize) {
      quantityButtons.forEach(function (button) {
        var quantity = button.dataset.optionValue;
        var selected = String(quantity) === String(state.quantity);
        var candidate = nativeSize ? variantForState(nativeSize, quantity) : variants.find(function (variant) {
          return String(variant.options[quantityIndex]) === String(quantity) && variant.available;
        });
        button.classList.toggle('acr-calc__btn--active', selected);
        button.classList.toggle('acr-calc__pricing-item--active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.disabled = !(candidate && candidate.available);
      });
    }

    function renderPricingRows(nativeSize) {
      pricingRows.forEach(function (row) {
        var quantity = row.dataset.optionValue;
        var variant = variantForState(nativeSize, quantity);
        var metrics = metricsForVariant(variant, quantity);
        var total = row.querySelector('[data-acrylic-sign-row-total]');
        var unit = row.querySelector('[data-acrylic-sign-row-unit]');
        total.textContent = metrics ? money(metrics.totalCents, calculator.dataset.currency) : 'Unavailable';
        unit.textContent = metrics ? money(metrics.priceEachCents, calculator.dataset.currency) + ' / sign' : 'No approved price';
      });
    }

    function render() {
      var recipeReady = calculator.dataset.recipeReady === 'true' && sizeIndex !== -1 && quantityIndex !== -1;
      var sizeValidation = currentSizeValidation();
      var nativeSize = sizeValidation.valid ? resolvedNativeSize() : '';
      var variant = recipeReady && nativeSize ? variantForState(nativeSize, state.quantity) : null;
      var quantity = Number(state.quantity);
      var metrics = Number.isInteger(quantity) && quantity > 0 ? metricsForVariant(variant, quantity) : null;

      state.variant = variant;
      state.metrics = metrics;
      state.sizeLabel = state.isCustomSize
        ? dimensionLabel(state.customWidth, state.customHeight)
        : dimensionLabel(parseDimensions(state.size).width, parseDimensions(state.size).height);

      customSizePanel.hidden = !state.isCustomSize;
      setSizeError(state.isCustomSize && !sizeValidation.valid ? sizeValidation.message : '');
      renderSizeButtons();
      renderQuantityButtons(nativeSize);
      renderPricingRows(nativeSize);
      labelValue('size', state.sizeLabel || '—');
      labelValue('quantity', state.quantity || '—');
      labelValue('shape', state.shapeLabel);
      labelValue('mounting', state.mountingLabel);
      labelValue('production', state.turnaroundDisplayLabel);
      calculator.querySelector('[data-acrylic-sign-summary="priceEach"]').textContent =
        metrics ? money(metrics.priceEachCents, calculator.dataset.currency) : 'Unavailable';
      calculator.querySelector('[data-acrylic-sign-summary="total"]').textContent =
        metrics ? money(metrics.totalCents, calculator.dataset.currency) : 'Unavailable';

      if (!recipeReady) {
        setStatus(calculator.dataset.recipeError, true);
      } else if (!sizeValidation.valid) {
        setStatus(sizeValidation.message, true);
      } else if (state.isCustomSize && !nativeSize) {
        setStatus('Pricing for this exact Custom Size is not configured in Shopify. Choose a supported exact size to continue.', true);
      } else if (!variant) {
        setStatus('This Size and Quantity combination is not configured in Shopify.', true);
      } else if (!variant.available) {
        setStatus('This configuration is currently unavailable.', true);
      } else if (!metrics) {
        setStatus('This configuration could not be priced safely.', true);
      } else if (!adjustmentVariants) {
        setStatus('Loading exact cart pricing…');
      } else if (!adding) {
        setStatus('');
      }

      submit.disabled = adding || !recipeReady || !sizeValidation.valid || !nativeSize ||
        !variant || !variant.available || !metrics || !adjustmentVariants;
      return !submit.disabled;
    }

    function updateUrl() {
      if (!state.variant || !root.history || typeof root.history.replaceState !== 'function') return;
      var destination = new URL(root.location.href);
      destination.searchParams.set('variant', String(state.variant.id));
      root.history.replaceState(root.history.state, '', destination.pathname + destination.search + destination.hash);
    }

    function refreshCart() {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('Cart refresh failed.');
          return response.json();
        })
        .then(function () {
          root.document.dispatchEvent(new CustomEvent('cart:refresh'));
          root.setTimeout(function () {
            var drawer = root.document.querySelector('cart-drawer');
            if (drawer && typeof drawer.show === 'function') drawer.show();
          }, 150);
        });
    }

    function submitConfiguredSign() {
      if (!render() || !root.__ZUG_UPLOAD) return;
      var bundle;
      try {
        bundle = root.ZugBannerPricing.buildAdjustmentBundle({
          uid: state.configUid,
          totalCents: state.metrics.totalCents,
          basePriceCents: Number(state.variant.price),
          baseVariantId: Number(state.variant.id),
          dollarVariantId: adjustmentVariants.dollar,
          centVariantId: adjustmentVariants.cent,
          baseProperties: propertiesFor(state, state.variant, calculator, state.metrics)
        });
      } catch (error) {
        setStatus('This Acrylic Sign could not be priced safely.', true);
        return;
      }

      adding = true;
      submit.setAttribute('aria-busy', 'true');
      setStatus('Adding your Acrylic Sign to cart…');
      render();
      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: bundle.items })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Acrylic Sign cart add failed.');
          return response.json();
        })
        .then(function () {
          root.__ZUG_UPLOAD = null;
          state.configUid = uid();
          return refreshCart();
        })
        .catch(function (error) {
          console.error('[ZUG Acrylic Sign] Add to cart failed.', error);
          setStatus('Your Acrylic Sign could not be added. Please try again or contact us for assistance.', true);
        })
        .finally(function () {
          adding = false;
          submit.removeAttribute('aria-busy');
          render();
        });
    }

    function openArtwork() {
      if (!render()) return;
      if (!root.ZUGUploadModal) {
        setStatus('Artwork tools could not initialize. Please reload the page.', true);
        return;
      }
      var dimensions = state.isCustomSize
        ? { width: state.customWidth, height: state.customHeight }
        : parseDimensions(state.size);
      root.ZUGUploadModal.open({
        calc_line_uid: state.configUid,
        product_handle: calculator.dataset.productHandle,
        variant_id: state.variant.id,
        quantity: Number(state.quantity),
        size_label: state.sizeLabel,
        target_width_in: dimensions.width,
        target_height_in: dimensions.height,
        shape: state.shape,
        material_key: 'acrylic',
        is_acrylic: true,
        acrylic_subtype: 'sign',
        onSaved: function () {
          render();
          submitConfiguredSign();
        },
        onCancel: function () { render(); },
        onSkip: function () { render(); }
      });
    }

    sizeButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        if (button.disabled) return;
        var isCustom = button.dataset.optionValue === 'custom';
        state.isCustomSize = isCustom;
        if (isCustom) {
          var currentDimensions = parseDimensions(state.size);
          if (Number.isFinite(currentDimensions.width) && Number.isFinite(currentDimensions.height)) {
            state.customWidth = currentDimensions.width;
            state.customHeight = currentDimensions.height;
            customWidthInput.value = formatDimension(state.customWidth);
            customHeightInput.value = formatDimension(state.customHeight);
          }
        } else {
          state.size = button.dataset.optionValue;
        }
        invalidateArtwork();
        render();
        updateUrl();
      });
    });

    function updateCustomDimensions() {
      state.customWidth = Number(customWidthInput.value);
      state.customHeight = Number(customHeightInput.value);
      invalidateArtwork();
      render();
      updateUrl();
    }

    customWidthInput.addEventListener('input', updateCustomDimensions);
    customHeightInput.addEventListener('input', updateCustomDimensions);
    customWidthInput.addEventListener('change', updateCustomDimensions);
    customHeightInput.addEventListener('change', updateCustomDimensions);

    quantityButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        if (button.disabled) return;
        state.quantity = button.dataset.optionValue;
        invalidateArtwork();
        render();
        updateUrl();
      });
    });

    shapeButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        shapeButtons.forEach(function (candidate) {
          var selected = candidate === button;
          candidate.classList.toggle('acr-calc__icon-btn--active', selected);
          candidate.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        state.shape = button.dataset.acrylicSignShape;
        state.shapeLabel = button.dataset.label;
        invalidateArtwork();
        render();
      });
    });

    mountingButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        mountingButtons.forEach(function (candidate) {
          var selected = candidate === button;
          candidate.classList.toggle('acr-calc__btn--active', selected);
          candidate.setAttribute('aria-checked', selected ? 'true' : 'false');
        });
        state.mounting = button.dataset.acrylicSignMounting;
        state.mountingLabel = button.dataset.label;
        state.mountingCents = Number(button.dataset.adjustmentCents);
        render();
      });
    });

    turnaroundButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        turnaroundButtons.forEach(function (candidate) {
          var selected = candidate === button;
          candidate.classList.toggle('acr-calc__btn--active', selected);
          candidate.setAttribute('aria-checked', selected ? 'true' : 'false');
        });
        state.turnaround = button.dataset.acrylicSignTurnaround;
        state.turnaroundLabel = button.dataset.label;
        state.turnaroundDisplayLabel = button.dataset.displayLabel;
        state.turnaroundMultiplier = Number(button.dataset.multiplier);
        var timeline = calculator.querySelector('.ptl');
        if (timeline && typeof timeline.ptlSetRange === 'function') {
          if (state.turnaround === 'rush_3_5') timeline.ptlSetRange(3, 5);
          else if (state.turnaround === 'rush_1_2') timeline.ptlSetRange(1, 2);
          else timeline.ptlSetRange(Number(calculator.dataset.productionMin), Number(calculator.dataset.productionMax));
        }
        render();
      });
    });

    if (pricingToggle && pricingList) {
      pricingToggle.addEventListener('click', function () {
        pricingListOpen = !pricingListOpen;
        pricingList.hidden = !pricingListOpen;
        pricingToggle.setAttribute('aria-expanded', pricingListOpen ? 'true' : 'false');
        pricingToggleText.textContent = pricingListOpen ? 'Hide pricing list' : 'View pricing list';
      });
    }

    submit.addEventListener('click', openArtwork);
    render();
    fetch(calculator.dataset.variantMapUrl, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Exact-price carriers could not be loaded.');
        return response.json();
      })
      .then(function (map) {
        adjustmentVariants = map && map.adjustment ? {
          dollar: Number(map.adjustment.dollar || map.adjustment.Dollar),
          cent: Number(map.adjustment.cent || map.adjustment.Cent)
        } : null;
        if (!adjustmentVariants || !adjustmentVariants.dollar || !adjustmentVariants.cent) {
          adjustmentVariants = null;
          throw new Error('Exact-price carriers are incomplete.');
        }
        render();
      })
      .catch(function (error) {
        console.error('[ZUG Acrylic Sign] Pricing initialization failed.', error);
        adjustmentVariants = null;
        render();
      });
  }

  function initializeAll() {
    if (!root.document) return;
    root.document.querySelectorAll('[data-zug-acrylic-sign]').forEach(initialize);
  }

  function start() {
    if (!root.document) return;
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
    } else {
      initializeAll();
    }
    root.document.addEventListener('shopify:section:load', initializeAll);
  }

  return {
    validateDimensions: validateDimensions,
    nativeSizeForDimensions: nativeSizeForDimensions,
    parseDimensions: parseDimensions,
    dimensionLabel: dimensionLabel,
    initializeAll: initializeAll,
    start: start
  };
});
